import gql from 'graphql-tag';

import {
    address,
    cancellationPolicy,
    clientLabel,
    clientOrder,
    clientVoucher,
    currency,
    fullOfficeClient,
    fullOfficeClientOrder,
    lectureInstance,
    lectureTerm,
    officeClient,
    officeClientTableGrid,
    officeConfig,
    officeEcomailIntegration,
    officeEmployee,
    officeEntryCredit,
    officeEntryPass,
    officeLectureTemplate,
    officeMailchimpIntegration,
    officeMailerLiteIntegration,
    officeNotificationConfig,
    officeNotificationMessages,
    officePaymentMethod,
    officePlace,
    officeSmartemailingIntegration,
    officeTerms,
    officeVoucher,
    organisationLabelFragment,
    organisationOffice,
    privateCourse,
    promoCodeOffice,
    promoCodePublic,
    publicCourse,
    publicCourseOverviewWithoutTerms,
    publicOfficeEmployee,
    rowOfficeStatsFragment,
    teacherStatsFragment,
    waitingPaymentValueCurrencyFragment,
    workshop,
} from '../fragments';

export const getAddressByOfficeId = gql`
    query GetAddressByOfficeId($officeId: Int!) {
        addressByOfficeId(officeId: $officeId) {
            ...AddressFragment
        }
    }
    ${address}
`;

export const getClientsByOfficeId = gql`
    query GetClientsByOfficeId(
        $officeId: Int!
        $limit: Int!
        $offset: Int
        $filters: ClientsByOfficeFiltersInput
        $sorter: ClientsByOfficeSorterInput
    ) {
        clientsByOfficeId(officeId: $officeId, limit: $limit, offset: $offset, filters: $filters, sorter: $sorter) {
            clients {
                ...OfficeClientTableGridFragment
            }
            totalCount
        }
    }
    ${officeClientTableGrid}
`;

export const getPrivateWorkshopsByOfficeId = gql`
    query GetPrivateWorkshopsByOfficeId($officeId: Int!, $filters: OfficePublicLecturesFiltersInput) {
        privateWorkshopsByOfficeId(officeId: $officeId, filters: $filters) {
            ...WorkshopFragment
            clientOrders {
                ...ClientOrderFragment
            }
        }
    }
    ${workshop}
    ${clientOrder}
`;

export const getPublicWorkshopsByOfficeId = gql`
    query GetPublicWorkshopsByOfficeId($officeId: Int!, $filters: OfficePublicLecturesFiltersInput) {
        publicWorkshopsByOfficeId(officeId: $officeId, filters: $filters) {
            ...WorkshopFragment
            clientOrders {
                ...ClientOrderFragment
            }
        }
    }
    ${workshop}
    ${clientOrder}
`;

export const getPublicCourseDetailByIdAndOfficeId = gql`
    query GetPublicCourseDetailByIdAndOfficeId($officeId: Int!, $courseId: Int!) {
        publicCourseDetailByOfficeId(officeId: $officeId, courseId: $courseId) {
            ...PublicCourseFragment
        }
    }
    ${publicCourse}
`;

export const getPrivateCourseDetailByIdAndOfficeId = gql`
    query GetPrivateCourseDetailByIdAndOfficeId($officeId: Int!, $courseId: Int!) {
        privateCourseDetailByOfficeId(officeId: $officeId, courseId: $courseId) {
            ...PrivateCourseFragment
        }
    }
    ${privateCourse}
`;

export const getPublicCoursesByOfficeId = gql`
    query GetPublicCoursesByOfficeId($officeId: Int!, $resultType: String!, $presentationType: String!) {
        publicCoursesByOfficeId(officeId: $officeId, resultType: $resultType, presentationType: $presentationType) {
            ...PublicCourseFragment
        }
    }
    ${publicCourse}
`;

export const getPublicCoursesOverviewWithoutTermsByOfficeId = gql`
    query GetPublicCoursesOverviewWithoutTermsByOfficeId(
        $officeId: Int!
        $resultType: String!
        $presentationType: String!
    ) {
        publicCoursesOverviewWithoutTermsByOfficeId(
            officeId: $officeId
            resultType: $resultType
            presentationType: $presentationType
        ) {
            ...PublicCourseOverviewWithoutTermsFragment
        }
    }
    ${publicCourseOverviewWithoutTerms}
`;

export const getPrivateWorkshopDetailByOfficeId = gql`
    query GetPrivateWorkshopDetailByOfficeId($officeId: Int!, $workshopId: Int!) {
        privateWorkshopDetailByOfficeId(officeId: $officeId, workshopId: $workshopId) {
            ...WorkshopFragment
            clientOrders {
                ...FullOfficeClientOrderFragment
            }
        }
    }
    ${workshop}
    ${fullOfficeClientOrder}
`;

export const getPublicWorkshopDetailByOfficeId = gql`
    query GetPublicWorkshopDetailByOfficeId($officeId: Int!, $workshopId: Int!) {
        publicWorkshopDetailByOfficeId(officeId: $officeId, workshopId: $workshopId) {
            ...WorkshopFragment
            clientOrders {
                ...ClientOrderFragment
            }
        }
    }
    ${workshop}
    ${clientOrder}
`;

export const getClientOrderByIdAndOfficeId = gql`
    query GetClientOrderByIdAndOfficeId($officeId: Int!, $clientOrderId: String!) {
        clientOrderByIdAndOfficeId(officeId: $officeId, clientOrderId: $clientOrderId) {
            ...FullOfficeClientOrderFragment
        }
    }
    ${fullOfficeClientOrder}
`;

export const getPublicCourseClientOrderById = gql`
    query GetPublicCourseClientOrderById($clientOrderGraphQlId: String!, $courseId: Int!) {
        publicCourseClientOrderById(clientOrderGraphQlId: $clientOrderGraphQlId, courseId: $courseId) {
            ...ClientOrderFragment
        }
    }
    ${clientOrder}
`;

export const getPublicWorkshopClientOrderById = gql`
    query GetPublicWorkshopClientOrderById($clientOrderGraphQlId: String!, $workshopId: Int!) {
        publicWorkshopClientOrderById(clientOrderGraphQlId: $clientOrderGraphQlId, workshopId: $workshopId) {
            clientOrder {
                ...ClientOrderFragment
            }
            workshop {
                ...WorkshopFragment
            }
        }
    }
    ${clientOrder}
    ${workshop}
`;

export const getClientOrdersByOfficeId = gql`
    query GetClientOrdersByOfficeId(
        $officeId: Int!
        $limit: Int!
        $offset: Int
        $filters: ClientOrdersByOfficeFiltersInput
    ) {
        clientOrdersByOfficeId(officeId: $officeId, limit: $limit, offset: $offset, filters: $filters) {
            orders {
                ...FullOfficeClientOrderFragment
                lectureTermInstance {
                    ...LectureTermFragment
                    lectureInstance {
                        ...LectureInstanceFragment
                    }
                }
            }
            totalCount
        }
    }
    ${fullOfficeClientOrder}
    ${lectureTerm}
    ${lectureInstance}
`;

export const getClientsByNameAndOfficeId = gql`
    query GetClientsByNameAndOfficeId($officeId: Int!, $searchedText: String!) {
        clientsByNameAndOfficeId(officeId: $officeId, searchedText: $searchedText) {
            ...OfficeClientFragment
        }
    }
    ${officeClient}
`;

export const getLectureTermBulkMessages = gql`
    query GetLectureTermBulkMessages($officeId: Int!, $lectureTermId: Int!) {
        lectureTermBulkMessages(officeId: $officeId, lectureTermId: $lectureTermId) {
            id
            messageId
            text
            datetime
            lectureTermId
        }
    }
`;

export const getWorkshopBulkMessages = gql`
    query GetWorkshopBulkMessages($officeId: Int!, $workshopId: Int!) {
        workshopBulkMessages(officeId: $officeId, workshopId: $workshopId) {
            id
            messageId
            text
            datetime
            workshopId
        }
    }
`;

export const getClientByEmailAndOfficeId = gql`
    query GetClientByEmailAndOfficeId($officeId: Int!, $email: String!) {
        clientByEmailAndOfficeId(officeId: $officeId, email: $email) {
            ...OfficeClientFragment
        }
    }
    ${officeClient}
`;

export const getOfficeClientById = gql`
    query GetOfficeClientById($clientId: Int!, $officeId: Int!) {
        officeClientById(clientId: $clientId, officeId: $officeId) {
            ...FullOfficeClientFragment
        }
    }
    ${fullOfficeClient}
`;

export const getOfficeClientLabelsByClientId = gql`
    query GetOfficeClientById($clientId: Int!, $officeId: Int!) {
        officeClientById(clientId: $clientId, officeId: $officeId) {
            labels {
                ...ClientLabelFragment
            }
        }
    }
    ${clientLabel}
`;

export const getEmployeesByOfficeId = gql`
    query GetEmployeesByOfficeId($officeId: Int!, $filters: EmployeesByOfficeFiltersInput) {
        employeesByOfficeId(officeId: $officeId, filters: $filters) {
            employees {
                ...OfficeEmployeeFragment
            }
            founderEmployeeId
        }
    }
    ${officeEmployee}
`;

export const getPublicFreelancerByOfficeId = gql`
    query GetPublicFreelancerByOfficeId($officeId: Int!) {
        publicFreelancerByOfficeId(officeId: $officeId) {
            ...PublicOfficeEmployeeFragment
        }
    }
    ${publicOfficeEmployee}
`;

export const getPublicStudioByOfficeId = gql`
    query GetPublicStudioByOfficeId($officeId: Int!) {
        publicStudioByOfficeId(officeId: $officeId) {
            ...PublicOfficeEmployeeFragment
        }
    }
    ${publicOfficeEmployee}
`;

export const getActiveEmployeeByOfficeId = gql`
    query GetActiveEmployeeByOfficeId($officeId: Int!, $employeeId: Int!) {
        activeEmployeeByOfficeId(officeId: $officeId, employeeId: $employeeId) {
            ...OfficeEmployeeFragment
        }
    }
    ${officeEmployee}
`;

export const getTermsByOfficeId = gql`
    query GetTermsByOfficeId($officeId: Int!) {
        termsByOfficeId(officeId: $officeId) {
            ...OfficeTermsFragment
        }
    }
    ${officeTerms}
`;

// WARN: this is public method, everything in this fragment will be visible to users
export const getOfficeByDomain = gql`
    query GetOfficeByDomain($domain: String!) {
        officeByDomain(domain: $domain) {
            ...OrganisationOfficeFragment
            officeConfig {
                ...OfficeConfigFragment
            }
        }
    }
    ${organisationOffice}
    ${officeConfig}
`;

export const getOfficeLabels = gql`
    query GetOfficeLabels($officeId: Long!) {
        officeLabels(officeId: $officeId) {
            ...LectureLabelOutputFragment
        }
    }
    ${organisationLabelFragment}
`;

export const getShowCoursesAndWorkshopsForOfficeId = gql`
    query GetShowCoursesAndWorkshopsForOfficeId($officeId: Long!) {
        showCoursesAndWorkshopsForOfficeId(officeId: $officeId) {
            showCourses
            showWorkshops
        }
    }
`;

export const getLectureTemplatesByOfficeId = gql`
    query GetLectureTemplatesByOfficeId($officeId: Int!) {
        lectureTemplatesByOfficeId(officeId: $officeId) {
            ...OfficeLectureTemplateFragment
            place {
                ...OfficePlaceFragment
            }
            lecturer {
                ...OfficeEmployeeFragment
            }
        }
    }
    ${officeLectureTemplate}
    ${officePlace}
    ${officeEmployee}
`;

export const getCancellationPolicyTemplatesByOfficeId = gql`
    query CancellationPolicyTemplatesByOfficeId($officeId: Int!) {
        cancellationPolicyTemplatesByOfficeId(officeId: $officeId) {
            ...CancellationPolicyFragment
        }
    }
    ${cancellationPolicy}
`;

export const getOfficeWithConfigByOfficeId = gql`
    query OfficeWithConfigByOfficeId($officeId: Long!) {
        officeById(officeId: $officeId) {
            ...OrganisationOfficeFragment
            officeConfig {
                ...OfficeConfigFragment
            }
        }
    }
    ${organisationOffice}
    ${officeConfig}
`;

export const getOfficeWithConfigByOfficeGraphQlId = gql`
    query OfficeWithConfigByOfficeGraphQlId($id: String!) {
        officeByGraphQlId(id: $id) {
            ...OrganisationOfficeFragment
            address {
                ...AddressFragment
            }
            officeConfig {
                ...OfficeConfigFragment
            }
        }
    }
    ${organisationOffice}
    ${address}
    ${officeConfig}
`;

export const getOfficeConfigById = gql`
    query OfficeConfigById($officeConfigId: Int!) {
        officeConfigById(officeConfigId: $officeConfigId) {
            ...OfficeConfigFragment
        }
    }
    ${officeConfig}
`;

export const getOfficeStatisticsActual = gql`
    query OfficeStatisticsActual($officeId: Int!, $statsFrom: DateTime!, $statsTo: DateTime!) {
        officeStatisticsActual(officeId: $officeId, statsFrom: $statsFrom, statsTo: $statsTo) {
            termsCount
            waitingPaymentExpectedRevenue {
                ...WaitingPaymentValueCurrencyFragment
            }
            termReservationsActualStats {
                reservationState
                attendaceType
                reservationCount
            }
        }
    }
    ${waitingPaymentValueCurrencyFragment}
`;

export const getOfficeIncomeStatistics = gql`
    query OfficeIncomeStatistics(
        $officeId: Int!
        $statsFrom: DateTime
        $statsTo: DateTime
        $withLastNineMonths: Boolean
    ) {
        officeIncomeStatistics(
            officeId: $officeId
            statsFrom: $statsFrom
            statsTo: $statsTo
            withLastNineMonths: $withLastNineMonths
        ) {
            tariffValidFrom
            count_CREDIT
            count_PASS
            count_LECTURE
            count_COURSE
            count_WORKSHOP
            count_VOUCHER
            statsFrom
            statsTo
            defaultCurrencyCode
            sum_LECTURE
            sum_COURSE
            sum_WORKSHOP
            sum_CREDIT
            sum_PASS
            sum_VOUCHER
            total_reservations
            lastNineMonthsStats {
                tariffValidFrom
                count_CREDIT
                count_PASS
                count_LECTURE
                count_COURSE
                count_WORKSHOP
                count_VOUCHER
                statsFrom
                statsTo
                defaultCurrencyCode
                sum_LECTURE
                sum_COURSE
                sum_WORKSHOP
                sum_CREDIT
                sum_PASS
                sum_VOUCHER
                total_reservations
            }
        }
    }
`;

export const getOfficeStatisticsRevenue = gql`
    query OfficeStatisticsRevenue($officeId: Int!, $statsFrom: DateTime!, $statsTo: DateTime!) {
        officeStatisticsRevenue(officeId: $officeId, statsFrom: $statsFrom, statsTo: $statsTo) {
            summaryStats {
                ...RowOfficeStatsOutputFragment
            }
            teachersStats {
                ...TeacherStatsFragment
            }
            workshopsStats {
                ...WorkshopFragment
                clientOrders {
                    ...ClientOrderFragment
                }
            }
            totalCoursesInRange {
                ...PrivateCourseFragment
            }
        }
    }
    ${rowOfficeStatsFragment}
    ${teacherStatsFragment}
    ${workshop}
    ${clientOrder}
    ${privateCourse}
`;

export const getOfficeEntryPassesByOfficeId = gql`
    query OfficeEntryPassesByOfficeId($officeId: Int!, $filters: OfficeEntryPassesFiltersInput) {
        officeEntryPassesByOfficeId(officeId: $officeId, filters: $filters) {
            data {
                ...OfficeEntryPassFragment
            }
            systemStatus
        }
    }
    ${officeEntryPass}
`;

export const getOfficeNotificationConfigByOfficeId = gql`
    query OfficeNotificationConfigByOfficeId($officeId: Int!) {
        officeNotificationConfigByOfficeId(officeId: $officeId) {
            ...OfficeNotificationConfigFragment
            messages {
                ...OfficeNotificationMessagesFragment
            }
        }
    }
    ${officeNotificationConfig}
    ${officeNotificationMessages}
`;

export const getOfficeSmartemailingIntegrationByOfficeId = gql`
    query OfficeSmartemailingIntegrationByOfficeId($officeId: Int!) {
        officeSmartemailingIntegration(officeId: $officeId) {
            ...OfficeSmartemailingIntegrationFragment
        }
    }
    ${officeSmartemailingIntegration}
`;

export const getOfficeMailerLiteIntegrationByOfficeId = gql`
    query OfficeMailerLiteIntegrationByOfficeId($officeId: Int!) {
        officeMailerLiteIntegration(officeId: $officeId) {
            ...OfficeMailerLiteIntegrationFragment
        }
    }
    ${officeMailerLiteIntegration}
`;

export const getOfficeEcomailIntegrationByOfficeId = gql`
    query OfficeEcomailIntegrationByOfficeId($officeId: Int!) {
        officeEcomailIntegration(officeId: $officeId) {
            ...OfficeEcomailIntegrationFragment
        }
    }
    ${officeEcomailIntegration}
`;

export const getOfficeMailchimpIntegrationByOfficeId = gql`
    query OfficeMailchimpIntegrationByOfficeId($officeId: Int!) {
        officeMailchimpIntegration(officeId: $officeId) {
            ...OfficeMailchimpIntegrationFragment
        }
    }
    ${officeMailchimpIntegration}
`;

export const getOfficeEntryCreditsByOfficeId = gql`
    query OfficeEntryCreditsByOfficeId($officeId: Int!, $filters: OfficeEntryCreditsFiltersInput) {
        officeEntryCreditsByOfficeId(officeId: $officeId, filters: $filters) {
            data {
                ...OfficeEntryCreditFragment
            }
            systemStatus
        }
    }
    ${officeEntryCredit}
`;

export const getOfficePaymentMethods = gql`
    query OfficePaymentMethods($officeId: Int!) {
        officePaymentMethods(officeId: $officeId) {
            ...OfficePaymentMethodFragment
        }
    }
    ${officePaymentMethod}
`;

export const getSupportedCurrencies = gql`
    query GetSupportedCurrencies {
        supportedCurrencies {
            ...CurrencyFragment
        }
    }
    ${currency}
`;

export const getUsersWithActiveCreditOrPassByOffice = gql`
    query UsersWithActiveCreditOrPassByOffice(
        $officeId: Int!
        $filters: UsersWithActiveCreditOrPassByOfficeFilterInput
    ) {
        usersWithActiveCreditOrPassByOffice(officeId: $officeId, filters: $filters) {
            withValidCreditSum
            withValidPassSum
            lastCreditValidityDate
            lastPassValidityDate
            clientsCredits {
                name
                organisationClientId
                validTo
            }
            clientsPass {
                name
                organisationClientId
                validTo
            }
        }
    }
`;

export const getPromoCodesByOfficeId = gql`
    query GetPromoCodesByOfficeId($officeId: Int!, $limit: Int!, $offset: Int) {
        promoCodesByOfficeId(officeId: $officeId, limit: $limit, offset: $offset) {
            promoCodes {
                ...PromoCodeOfficeFragment
            }
            totalCount
            totalActive
        }
    }
    ${promoCodeOffice}
`;

export const vouchersByOfficeId = gql`
    query GetVouchersByOfficeId($officeId: Int!) {
        vouchersByOfficeId(officeId: $officeId) {
            ...OfficeVoucherFragment
        }
    }
    ${officeVoucher}
`;
